import {
    ProductVariantSizeDto,
    ProductVariantSizesDto,
    SimpleProductVariantSize
} from '../../../types/ProductVariant.types';

export type ElementSizes = {
    sizesDrawAreaWidth: number;
    sizesDrawAreaHeight: number;
    minAirflowInProductVariantSizes: number;
    maxAirflowInProductVariantSizes: number;
    height: number;
};

export const getElementSizes = (
    designViewMode: boolean,
    productVariantSizes?: ProductVariantSizesDto
) => {
    const minBlockHeight = 25;
    const maxBlockHeight = 40;
    const width = designViewMode ? 300 : 500;
    const height = designViewMode ? 300 : 500;
    const minAirflow = Math.min(
        ...(productVariantSizes?.sizes.map((s: ProductVariantSizeDto) =>
            getMinAirFlow(s)
        ) ?? [])
    );
    const maxAirflow = Math.max(
        ...(productVariantSizes?.sizes.map((s: ProductVariantSizeDto) =>
            getMaxAirFlow(s)
        ) ?? [])
    );

    const elementSizes: ElementSizes = {
        sizesDrawAreaWidth: width,
        sizesDrawAreaHeight: height,
        minAirflowInProductVariantSizes: Number(minAirflow),
        maxAirflowInProductVariantSizes: Number(maxAirflow),
        height: Math.min(
            Math.max(
                minBlockHeight,
                height / (productVariantSizes?.sizes?.length || 1)
            ),
            maxBlockHeight
        )
    };

    return elementSizes;
};

export const getMinAirFlow = (size: ProductVariantSizeDto) => {
    if (isExtractAirOnly(size) && size?.extractAirflow?.min) {
        return !isNaN(size?.extractAirflow?.min)
            ? size?.extractAirflow?.min
            : 0;
    } else if (isSupplyAirOnly(size) && size?.supplyAirflow?.min) {
        return !isNaN(size?.supplyAirflow?.min) ? size?.supplyAirflow?.min : 0;
    } else {
        const minAirflow =
            size.supplyAirflow !== undefined &&
            size.extractAirflow !== undefined &&
            size.supplyAirflow?.min <= size.extractAirflow?.min
                ? size.supplyAirflow?.min
                : size.extractAirflow?.min;
        return minAirflow !== undefined && !isNaN(minAirflow) ? minAirflow : 0;
    }
};

export const getMaxAirFlow = (size: ProductVariantSizeDto) => {
    if (isExtractAirOnly(size) && size?.extractAirflow?.max) {
        return !isNaN(size?.extractAirflow?.max)
            ? size?.extractAirflow?.max
            : 0;
    } else if (isSupplyAirOnly(size) && size?.supplyAirflow?.max) {
        return !isNaN(size?.supplyAirflow?.max) ? size?.supplyAirflow?.max : 0;
    } else {
        const minAirflow =
            size.supplyAirflow !== undefined &&
            size.extractAirflow !== undefined &&
            size.supplyAirflow?.max <= size.extractAirflow?.max
                ? size.supplyAirflow?.max
                : size.extractAirflow?.max;
        return minAirflow !== undefined && !isNaN(minAirflow) ? minAirflow : 0;
    }
};

export const sizeIsWithinAirflowRange = (
    id: string,
    sizes: SimpleProductVariantSize[]
) => sizes.some((size) => size.sizeId === id);

const getNewMaxAirflow = (sizeToCompare: ProductVariantSizeDto) => {
    if (sizeToCompare.extractAirflow && !sizeToCompare.supplyAirflow) {
        return sizeToCompare.extractAirflow.max;
    } else if (sizeToCompare.supplyAirflow && !sizeToCompare.extractAirflow) {
        return sizeToCompare.supplyAirflow.max;
    } else if (sizeToCompare.supplyAirflow && sizeToCompare.extractAirflow) {
        return sizeToCompare.supplyAirflow.max;
    }
    return;
};

const getNewMinAirflow = (sizeToCompare: ProductVariantSizeDto) => {
    if (sizeToCompare.extractAirflow && !sizeToCompare.supplyAirflow) {
        return sizeToCompare.extractAirflow.min;
    } else if (sizeToCompare.supplyAirflow && !sizeToCompare.extractAirflow) {
        return sizeToCompare.supplyAirflow.min;
    } else if (sizeToCompare.supplyAirflow && sizeToCompare.extractAirflow) {
        return sizeToCompare.supplyAirflow.min;
    }
    return;
};

export const getClosestAirflowsForSizeOutOfRange = (
    sizeId: string,
    allProductVariantSizes: ProductVariantSizeDto[],
    supplyAirFlowInput: number,
    extractAirFlowInput: number
) => {
    const sizeToCompare = allProductVariantSizes.find(
        (a) => a.sizeId === sizeId
    );
    if (!sizeToCompare) {
        return;
    }

    if (isSupplyAirOnly(sizeToCompare)) {
        if (
            sizeToCompare?.supplyAirflow?.max &&
            sizeToCompare.supplyAirflow.max < supplyAirFlowInput
        ) {
            const newMaxAirflow = getNewMaxAirflow(sizeToCompare);
            if (!newMaxAirflow) {
                return;
            }
            return {
                newSupplyAirflow: parseFloat((newMaxAirflow * 0.8).toFixed(3))
            };
        }
        if (
            sizeToCompare?.supplyAirflow?.min &&
            sizeToCompare.supplyAirflow.min > supplyAirFlowInput
        ) {
            const newMinAirflow = getNewMinAirflow(sizeToCompare);
            if (!newMinAirflow) {
                return;
            }
            return {
                newSupplyAirflow: parseFloat((newMinAirflow * 1.2).toFixed(3))
            };
        }
    }
    if (isExtractAirOnly(sizeToCompare)) {
        if (
            sizeToCompare?.extractAirflow?.max &&
            sizeToCompare.extractAirflow.max < extractAirFlowInput
        ) {
            const newMaxAirflow = getNewMaxAirflow(sizeToCompare);
            if (!newMaxAirflow) {
                return;
            }
            return {
                newExtractAirflow: parseFloat((newMaxAirflow * 0.8).toFixed(3))
            };
        }
        if (
            sizeToCompare?.extractAirflow?.min &&
            sizeToCompare.extractAirflow.min > extractAirFlowInput
        ) {
            const newMinAirflow = getNewMinAirflow(sizeToCompare);
            if (!newMinAirflow) {
                return;
            }
            return {
                newExtractAirflow: parseFloat((newMinAirflow * 1.2).toFixed(3))
            };
        }
    }

    if (
        (sizeToCompare?.extractAirflow?.max &&
            sizeToCompare.extractAirflow.max < extractAirFlowInput) ||
        (sizeToCompare?.supplyAirflow?.max &&
            sizeToCompare.supplyAirflow.max < supplyAirFlowInput)
    ) {
        const newMaxAirflow = getNewMaxAirflow(sizeToCompare);
        if (!newMaxAirflow) {
            return;
        }
        return {
            newExtractAirflow: parseFloat((newMaxAirflow * 0.8).toFixed(3)),
            newSupplyAirflow: parseFloat((newMaxAirflow * 0.8).toFixed(3))
        };
    }

    if (
        (sizeToCompare?.extractAirflow?.min &&
            sizeToCompare.extractAirflow.min > extractAirFlowInput) ||
        (sizeToCompare?.supplyAirflow?.min &&
            sizeToCompare.supplyAirflow.min > supplyAirFlowInput)
    ) {
        const newMinAirflow = getNewMinAirflow(sizeToCompare);
        if (!newMinAirflow) {
            return;
        }
        return {
            newExtractAirflow: parseFloat((newMinAirflow * 1.2).toFixed(3)),
            newSupplyAirflow: parseFloat((newMinAirflow * 1.2).toFixed(3))
        };
    }

    return {
        newExtractAirflow: extractAirFlowInput,
        newSupplyAirflow: supplyAirFlowInput
    };
};

export const getProductVariantSizesWithinAirflowRange = (
    allProductVariantSizes: ProductVariantSizeDto[],
    supplyAirflowInput: number,
    extractAirflowInput: number
) => {
    const availableRanges = allProductVariantSizes.filter((size) => {
        if (size?.supplyAirflow !== null && size?.extractAirflow !== null) {
            return (
                size.supplyAirflow?.min &&
                supplyAirflowInput >= size.supplyAirflow.min &&
                size.supplyAirflow?.max &&
                supplyAirflowInput <= size.supplyAirflow.max &&
                size.extractAirflow?.min &&
                extractAirflowInput >= size.extractAirflow.min &&
                size.extractAirflow?.max &&
                extractAirflowInput <= size.extractAirflow.max
            );
        } else if (size?.supplyAirflow === null) {
            return (
                size?.extractAirflow?.min &&
                extractAirflowInput >= size.extractAirflow.min &&
                size?.extractAirflow?.max &&
                extractAirflowInput <= size.extractAirflow.max
            );
        } else if (size?.extractAirflow === null) {
            return (
                size?.supplyAirflow?.min &&
                supplyAirflowInput >= size.supplyAirflow.min &&
                size?.supplyAirflow?.max &&
                supplyAirflowInput <= size.supplyAirflow.max
            );
        }
    });

    return availableRanges;
};

export const isSupplyAirOnly = (size: ProductVariantSizeDto) =>
    typeof size.extractAirflow?.min !== 'number' &&
    typeof size.extractAirflow?.max !== 'number' &&
    typeof size.supplyAirflow?.min === 'number' &&
    typeof size.supplyAirflow?.max === 'number';

export const isExtractAirOnly = (size: ProductVariantSizeDto) =>
    typeof size.supplyAirflow?.min !== 'number' &&
    typeof size.supplyAirflow?.max !== 'number' &&
    typeof size.extractAirflow?.min === 'number' &&
    typeof size.extractAirflow?.max === 'number';

export const noAirflowValuesExists = (size: ProductVariantSizeDto) => {
    if (
        typeof size.extractAirflow?.min !== 'number' &&
        typeof size.extractAirflow?.max !== 'number' &&
        typeof size.supplyAirflow?.min !== 'number' &&
        typeof size.supplyAirflow?.max !== 'number'
    ) {
        return true;
    }
    return false;
};
